// Branch/edge derivation for the Solution Studio diagram editor (§8).
//
// Single source of truth for "what are a block's exits" (branchModel) and "how is a branch
// labelled/coloured" (edgeLabel / edgeColorKind / styleForEdge), keyed off the Flow DSL edge shape
// `{ from, to, kind, condition?, errorType? }` (see lambda/solnstudio/knowledge-pack DSL exemplars).
//
// This mirrors the AWS Export viewer's edge presentation (diagrams/contactFlowReactFlowParser.js
// getConditionLabel/getEdgeColor + ContactFlowNodes.js per-branch labelled handles) but reads the DSL
// instead of the Connect describe-flow JSON, so the SS editor can render the same colour/dash/label
// treatment AND stay editable. Framework-free (no React) so it is unit-testable in isolation.

import { branches, isTerminal, nodeMeta } from './flowCatalog';

// Branch "kind" → line colour + dash. Success green / condition blue / error red-dashed, matching the
// Export viewer's #4CAF50 / #2196F3 / #F44336 (tuned slightly toward the Mantine palette used by the
// node accents). `dash` is a React Flow `strokeDasharray` string (undefined = solid).
export const EDGE_KIND_STYLE = {
  success:   { color: '#2f9e44', dash: undefined }, // green
  condition: { color: '#1c7ed6', dash: undefined }, // blue
  error:     { color: '#e03131', dash: '6 4' },     // red, dashed
};

// Friendly text for the common Connect error types (the rest fall back to a camelCase split).
const ERROR_LABELS = {
  NoMatchingCondition: 'No match',
  NoMatchingError: 'Error',
  InputTimeLimitExceeded: 'Timeout',
  InvalidPhoneNumber: 'Invalid number',
  InvalidCallbackNumber: 'Invalid number',
  ConditionEvaluationFailed: 'Eval failed',
};

// Loop block condition operands → short labels (Connect models loop branches as Equals conditions).
const LOOP_LABELS = { ContinueLooping: 'Continue', DoneLooping: 'Done' };

function splitCamel(s) {
  return String(s || '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

/** Humanise an error type for a branch label. */
export function errorLabel(errorType) {
  if (!errorType) return 'Error';
  return ERROR_LABELS[errorType] || splitCamel(errorType);
}

/**
 * Human-readable text for a condition object `{ Operator, Operands }`. Mirrors the Export viewer's
 * getConditionLabel: a bare DTMF digit becomes "Press N", a string equals becomes `= "X"`, numeric
 * comparisons render with their operator. Loop sentinels (Continue/Done) get short names.
 */
export function conditionLabel(condition) {
  if (!condition) return '';
  const { Operator, Operands } = condition;
  const op0 = Operands && Operands.length ? Operands[0] : null;
  if (op0 == null) return Operator || '';
  if (LOOP_LABELS[op0]) return LOOP_LABELS[op0];
  if (Operator === 'Equals') return /^\d+$/.test(String(op0)) ? `Press ${op0}` : `= "${op0}"`;
  if (Operator === 'NumberLessThan') return `< ${op0}`;
  if (Operator === 'NumberGreaterThan') return `> ${op0}`;
  if (Operator === 'NumberEqualTo') return `= ${op0}`;
  if (Operator === 'NumberGreaterOrEqualTo') return `>= ${op0}`;
  if (Operator === 'NumberLessOrEqualTo') return `<= ${op0}`;
  if (Operands.length) return `${Operator}: ${Operands.join(', ')}`;
  return Operator || '';
}

/** Classify an edge into a style bucket: 'success' | 'condition' | 'error'. `default` ⇒ success. */
export function edgeColorKind(edge) {
  if (!edge) return 'success';
  if (edge.errorType || edge.kind === 'error') return 'error';
  if (edge.condition || edge.kind === 'condition') return 'condition';
  return 'success';
}

/**
 * Display label for an edge/branch — the ACCURATE exit condition, not the bare `kind`. Prefers the
 * structured `condition`/`errorType`; a linear success step is unlabelled (no clutter). Falls back to
 * a hand-typed `label`/`kind` for older DSL that only carries the kind string.
 */
export function edgeLabel(edge) {
  if (!edge) return '';
  if (edge.condition) return conditionLabel(edge.condition) || edge.label || 'Condition';
  if (edge.errorType) return errorLabel(edge.errorType);
  const kind = edge.kind || 'default';
  if (kind === 'condition') return edge.label && edge.label !== 'condition' ? edge.label : 'Condition';
  if (kind === 'error') return edge.label && edge.label !== 'error' ? edge.label : 'Error';
  // default / success — a linear step needs no label.
  return '';
}

/**
 * Stable handle id for a branch, so each exit gets its OWN connector and an edge re-binds to the same
 * handle across reloads (derived from condition operands / error type, not a random index).
 */
export function branchKey(edge) {
  const kind = edgeColorKind(edge);
  if (kind === 'error') return `err:${edge.errorType || edge.label || 'error'}`;
  if (kind === 'condition') {
    const ops = edge.condition && edge.condition.Operands ? edge.condition.Operands.join('|') : (edge.label || 'cond');
    return `cond:${ops}`;
  }
  return 'default';
}

// Curated extra exits to SHOW even before they are wired, so an engineer can drag a block's real
// branches (a freshly-added Get Input needs its No-match / Timeout connectors). Edge-derived branches
// always take precedence; these only fill gaps. Handles with no edge are harmless connection points.
const err = (errorType, label) => ({ key: `err:${errorType}`, kind: 'error', errorType, label: label || errorLabel(errorType) });
const cond = (operands, label) => ({ key: `cond:${operands.join('|')}`, kind: 'condition', condition: { Operator: 'Equals', Operands: operands }, label });

const STANDARD_BRANCHES = {
  GetParticipantInput: () => [err('NoMatchingCondition', 'No match'), err('InputTimeLimitExceeded', 'Timeout')],
  ConnectParticipantWithLexBot: () => [err('NoMatchingError')],
  Compare: () => [err('NoMatchingCondition', 'No match')],
  CheckHoursOfOperation: () => [err('NoMatchingError')],
  CheckMetricData: () => [err('NoMatchingError')],
  Loop: () => [cond(['ContinueLooping'], 'Continue'), cond(['DoneLooping'], 'Done')],
};

// Linear (non-branching) blocks that still expose a failure path, so their error connector is offered.
const HAS_ERROR_BRANCH = new Set([
  'InvokeLambdaFunction', 'GetCustomerProfile', 'AssociateContactToCustomerProfile', 'EvaluateDataTableValues',
  'GetMetricData', 'TransferContactToQueue', 'TransferToFlow', 'TransferParticipantToThirdParty',
  'CreateCallbackContact',
]);

/** The standard (catalog-derived) exits for a block type, shown even when not yet wired. */
function standardBranches(type) {
  if (isTerminal(type)) return [];
  const fn = STANDARD_BRANCHES[type];
  if (fn) return fn();
  const out = [];
  if (!branches(type)) out.push({ key: 'default', kind: 'success', label: '' });
  if (HAS_ERROR_BRANCH.has(type)) out.push(err('NoMatchingError'));
  return out;
}

const KIND_RANK = { success: 0, condition: 1, error: 2 };

/**
 * Ordered list of a node's exits `[{ key, label, kind, condition?, errorType? }]` — the UNION of its
 * actual outgoing edges (authoritative) and the block type's standard exits (so unwired branches still
 * get a connector). Ordered success → conditions → errors so the handles fan out predictably.
 */
export function branchModel(type, outEdges) {
  const fromEdges = (outEdges || []).map((e) => ({
    key: branchKey(e),
    label: edgeLabel(e) || (edgeColorKind(e) === 'success' ? '' : edgeColorKind(e)),
    kind: edgeColorKind(e),
    condition: e.condition,
    errorType: e.errorType,
  }));
  const seen = new Set();
  const merged = [];
  for (const b of [...fromEdges, ...standardBranches(type)]) {
    if (seen.has(b.key)) continue;
    seen.add(b.key);
    merged.push(b);
  }
  if (merged.length === 0 && !isTerminal(type)) merged.push({ key: 'default', kind: 'success', label: '' });
  return merged
    .map((b, i) => ({ ...b, _i: i }))
    .sort((a, b) => (KIND_RANK[a.kind] - KIND_RANK[b.kind]) || (a._i - b._i))
    .map(({ _i, ...b }) => b);
}

/** React Flow line style for an edge: `{ color, dash }` from its style bucket. */
export function styleForEdge(edge) {
  return EDGE_KIND_STYLE[edgeColorKind(edge)] || EDGE_KIND_STYLE.success;
}

/**
 * Reconstruct the DSL edge fields (`kind`, `condition`, `errorType`) for a connection made by dragging
 * from a specific branch handle — looks the branch up in the source node's branchModel so the new edge
 * carries the RIGHT semantics (a Press-1 condition, a Timeout error) rather than a blanket "success".
 */
export function edgeDataForBranch(branch) {
  if (!branch) return { kind: 'default' };
  if (branch.kind === 'error') return { kind: 'error', errorType: branch.errorType, branchKey: branch.key };
  if (branch.kind === 'condition') return { kind: 'condition', condition: branch.condition, branchKey: branch.key };
  return { kind: 'default', branchKey: branch.key };
}

/** Friendly source-node label for an edge (used by the edge editor panel). */
export function nodeLabelForType(type) {
  return nodeMeta(type).label;
}
